import React, { useState, useEffect, useRef } from 'react';
import { Swiper, SwiperSlide, useSwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay, EffectFade, EffectCube, EffectCoverflow, EffectFlip } from 'swiper/modules';
import { publicAPI } from '../../services/api';
import { FiVolume2, FiVolumeX } from 'react-icons/fi';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/effect-fade';
import 'swiper/css/effect-cube';
import 'swiper/css/effect-coverflow';
import 'swiper/css/effect-flip';
import './Home.css';

const BRANDS_PREVIEW_COUNT = 5;
const VIDEO_SLIDE_DURATION_MS = 600000; // 10 minutos, para que el video no sea cortado por el autoplay

const Home = () => {
  const [banners, setBanners] = useState([]);
  const [config, setConfig] = useState({ 
    carousel_effect: 'slide',
    carousel_speed: 1000,
    default_banner_duration: 5000
  });
  const [swiperInstance, setSwiperInstance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [globalMuted, setGlobalMuted] = useState(true);

  useEffect(() => {
    Promise.all([
      publicAPI.getBanners(),
      publicAPI.getConfiguration()
    ])
      .then(([bannersRes, configRes]) => {
        setBanners(bannersRes.data);
        if (configRes.data) {
          setConfig(prev => ({ ...prev, ...configRes.data }));
        }
        setLoading(false);
      })
      .catch((error) => {
        console.error('Error loading data:', error);
        setLoading(false);
      });
  }, []);

  if (loading) {
    return <div className="loading">Cargando...</div>;
  }

  return (
    <div className="home" style={{ color: 'rgb(116, 70, 49)' }}>
      {/* Carrusel de Banners */}
      <section className="carousel-section">
        <Swiper
          onSwiper={setSwiperInstance}
          modules={[Navigation, Pagination, Autoplay, EffectFade, EffectCube, EffectCoverflow, EffectFlip]}
          spaceBetween={0}
          slidesPerView={1}
          effect={config.carousel_effect}
          speed={config.carousel_speed} // Velocidad de transición (Retardo del efecto)
          navigation
          pagination={{ clickable: true }}
          autoplay={{
            delay: config.default_banner_duration, // Duración por defecto
            disableOnInteraction: false,
          }}
          loop={banners.length > 1}
          className="banner-carousel"
        >
          {banners.map((banner) => {
            const isVideo = banner.file_type === 'video';
            // Si es video, damos un tiempo muy largo (10 min) para que el autoplay no lo corte.
            // Si es imagen, usamos la duración configurada.
            const slideDelay = isVideo ? VIDEO_SLIDE_DURATION_MS : (banner.duration || config.default_banner_duration);

            return (
              <SwiperSlide key={banner.id} data-swiper-autoplay={slideDelay}>
                <div className="banner-slide">
                  {isVideo ? (
                    <VideoBanner
                      src={banner.file_path}
                      muted={globalMuted}
                      setMuted={setGlobalMuted}
                      onEnded={() => swiperInstance && swiperInstance.slideNext()}
                    />
                  ) : (
                    <img
                      src={banner.file_path}
                      alt={banner.title || 'Banner'}
                      className="banner-media"
                    />
                  )}
                </div>
              </SwiperSlide>
            );
          })}
        </Swiper>
      </section>

      {/* Sección de Bienvenida */}
      <section className="welcome-section">
        <div className="container">
          <h1>Bienvenido a Representaciones IMDI</h1>
          <p className="welcome-text">
            Somos tu aliado estratégico en el sector automotriz. Nos especializamos en la distribución a nivel nacional 
            de repuestos de alta gama, garantizando soluciones integrales con el respaldo de las mejores marcas del mercado.
            Calidad, confianza y alcance en cada entrega.
          </p>
        </div>
      </section>

      {/* Sección de Instagram Reels */}
      <section className="reels-section">
        <div className="container">
          <h2>Novedades</h2>
          <ReelsSection />
        </div>
      </section>

      {/* Sección de Marcas Destacadas */}
      <section className="brands-preview">
        <div className="container">
          <h2>Nuestras Marcas</h2>
          <BrandsPreview />
        </div>
      </section>
    </div>
  );
};

const BrandsPreview = () => {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    publicAPI.getBrands()
      .then((response) => {
        setBrands(response.data.slice(0, BRANDS_PREVIEW_COUNT));
      })
      .catch((error) => {
        console.error('Error loading brands:', error);
        setError('No se pudieron cargar las marcas.');
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div>Cargando marcas...</div>;
  if (error) return <div>{error}</div>;

  return (
    <div className="brands-grid">
      {brands.map((brand) => (
        <div key={brand.id} className="brand-card">
          <div className="brand-logo-wrapper">
            {brand.logo_path && (
              <img
                src={brand.logo_path}
                alt={brand.name}
                className="brand-logo"
              />
            )}
          </div>
          <h3>{brand.name}</h3>
        </div>
      ))}
    </div>
  );
};

const ReelsSection = () => {
  const [reels, setReels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    publicAPI.getInstagramReels()
      .then((response) => {
        setReels(response.data);
      })
      .catch((error) => {
        console.error('Error loading reels:', error);
        setError('No se pudieron cargar los reels de Instagram.');
      })
      .finally(() => {
        setLoading(false);
      })
  }, []);

  useEffect(() => {
    if (reels.length > 0) {
      // Cargar script de Instagram si no existe, o procesar si ya existe
      if (window.instgrm) {
        window.instgrm.Embeds.process();
      } else {
        const scriptId = 'instagram-embed-script';
        if (!document.getElementById(scriptId)) {
          const script = document.createElement('script');
          script.id = scriptId;
          script.src = '//www.instagram.com/embed.js';
          script.async = true;
          document.body.appendChild(script);
        }
      }
    }
  }, [reels]);

  if (loading) return <div>Cargando reels...</div>;
  if (error) return <div>{error}</div>;
  if (reels.length === 0) return <div>No hay reels para mostrar.</div>;

  return (
    <div className="reels-grid">
      {reels.map((reel) => (
        <div key={reel.id} className="reel-wrapper">
          <div dangerouslySetInnerHTML={{ __html: reel.embed_code }} />
        </div>
      ))}
    </div>
  );
};

const VideoBanner = ({ src, onEnded, muted, setMuted }) => {
  const swiperSlide = useSwiperSlide();
  const videoRef = useRef(null);

  useEffect(() => {
    // --- CORRECCIÓN: Sincronización forzada del sonido ---
    // Esto asegura que cuando cambies el estado, el video realmente se desmutee
    if (videoRef.current) {
      videoRef.current.muted = muted;
    }
  }, [muted]);

  useEffect(() => {
    if (videoRef.current) {
      if (swiperSlide.isActive) {
        videoRef.current.currentTime = 0; // Reiniciar video al aparecer
        videoRef.current.play().catch(() => {});
      } else {
        videoRef.current.pause(); // Pausar si no está visible
      }
    }
  }, [swiperSlide.isActive]);

  return (
    <div className="video-container" style={{ width: '100%', height: '100%', position: 'relative' }}>
      <video
        ref={videoRef}
        src={src}
        className="banner-media"
        muted={muted}
        playsInline
        onEnded={onEnded}
      />
      <button 
        className="mute-btn"
        onClick={(e) => { e.stopPropagation(); setMuted(!muted); }}
      >
        {muted ? <><FiVolumeX /> Activar Sonido</> : <><FiVolume2 /> Silenciar</>}
      </button>
    </div>
  );
};

export default Home;
